import { AutoAwesomeOutlined, Close, CloseFullscreen, OpenInFull, Send } from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';

import useTranslate from 'hooks/useTranslate';
import type { ChatReference } from 'types';

export type ComposerSize = 'compact' | 'mid' | 'full';

interface ChatComposerProps {
    hasAccess: boolean;
    isSending: boolean;
    isSuggestingReply: boolean;
    disabled?: boolean;
    canRetryReplySuggestion: boolean;
    canSuggestReply: boolean;
    messageText: string;
    references?: ChatReference[];
    size: ComposerSize;
    suggestionHint?: string;
    onMessageTextChange: (text: string) => void;
    onSizeChange: (size: ComposerSize) => void;
    onSend: (text: string, references?: ChatReference[]) => void;
    onSuggestReply: () => void;
}

interface TextSelection {
    start: number;
    end: number;
}

const NEXT_COMPOSER_SIZE: Record<ComposerSize, ComposerSize> = {
    compact: 'mid',
    mid: 'full',
    full: 'compact',
};

const COMPOSER_SIZE_LABEL: Record<ComposerSize, string> = {
    compact: 'expandMessageInputMid',
    mid: 'expandMessageInputFull',
    full: 'collapseMessageInput',
};

const ACTION_BUTTON_SX = { width: 40, height: 40 } as const;

const SEND_BUTTON_SX = {
    ...ACTION_BUTTON_SX,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { bgcolor: 'primary.dark' },
    '&.Mui-disabled': {
        bgcolor: 'action.disabledBackground',
        color: 'action.disabled',
    },
} as const;

const getComposerFlex = (size: ComposerSize, isStacked: boolean) => {
    if (size === 'full') {
        return '1 1 0px';
    }

    return isStacked ? '0 0 42%' : '0 0 auto';
};

const readSelection = (field: HTMLTextAreaElement | null): TextSelection | null =>
    field ? { start: field.selectionStart, end: field.selectionEnd } : null;

const applySelection = (field: HTMLTextAreaElement | null, selection: TextSelection | null) => {
    if (!field || !selection) {
        return;
    }

    field.focus();
    field.setSelectionRange(selection.start, selection.end);
};

const exceedsSingleLine = (field: HTMLTextAreaElement, text: string) =>
    text.includes('\n') || field.scrollHeight > field.clientHeight + 2;

export const ChatComposer: FC<ChatComposerProps> = ({
    hasAccess,
    isSending,
    isSuggestingReply,
    disabled = false,
    canRetryReplySuggestion,
    canSuggestReply,
    messageText,
    references,
    size,
    suggestionHint,
    onMessageTextChange,
    onSizeChange,
    onSend,
    onSuggestReply,
}) => {
    const { translate } = useTranslate();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const wasSendingRef = useRef(isSending);
    const skipAutoExpandRef = useRef(false);
    const selectionRef = useRef<TextSelection | null>(null);
    const [activeRefs, setActiveRefs] = useState<ChatReference[]>(references ?? []);
    const [isMultiline, setIsMultiline] = useState(false);
    const isDisabled = disabled || isSending || !hasAccess;
    const isSuggestionDisabled = isDisabled || isSuggestingReply;
    const isStacked = size !== 'compact' || isMultiline;
    const nextSizeLabel = translate(COMPOSER_SIZE_LABEL[size]);

    useEffect(() => {
        if (references?.length) {
            setActiveRefs(references);
        }
    }, [references]);

    useLayoutEffect(() => {
        const field = inputRef.current;
        if (
            size !== 'compact' ||
            isMultiline ||
            skipAutoExpandRef.current ||
            !field ||
            !messageText ||
            !exceedsSingleLine(field, messageText)
        ) {
            return;
        }

        selectionRef.current = readSelection(field);
        setIsMultiline(true);
    }, [isMultiline, messageText, size]);

    useLayoutEffect(() => {
        applySelection(inputRef.current, selectionRef.current);
    }, [isStacked]);

    useEffect(() => {
        const shouldRefocus = wasSendingRef.current && !isSending && hasAccess && !disabled;
        wasSendingRef.current = isSending;

        if (!shouldRefocus) {
            return;
        }

        const frame = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(frame);
    }, [disabled, hasAccess, isSending]);

    const rememberSelection = (field: EventTarget | null) => {
        selectionRef.current = readSelection(field as HTMLTextAreaElement | null);
    };

    const handleSend = () => {
        const trimmed = messageText.trim();
        if (!trimmed || isDisabled) {
            return;
        }

        onSend(trimmed, activeRefs.length ? activeRefs : undefined);
        setActiveRefs([]);
        setIsMultiline(false);
        skipAutoExpandRef.current = false;
        onSizeChange('compact');
    };

    const handleSizeChange = () => {
        const nextSize = NEXT_COMPOSER_SIZE[size];
        selectionRef.current = readSelection(inputRef.current);

        if (nextSize === 'compact') {
            skipAutoExpandRef.current = true;
            setIsMultiline(false);
        }

        onSizeChange(nextSize);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    return (
        <Box
            sx={(theme) => ({
                flex: getComposerFlex(size, isStacked),
                minHeight: isStacked ? 168 : 0,
                display: 'flex',
                flexDirection: 'column',
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 1.25, sm: 2 },
                pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 2 },
                transition: theme.transitions.create(['flex-basis', 'min-height'], {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeInOut,
                }),
            })}
        >
            {activeRefs.length ? (
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1.25 }}>
                    {activeRefs.map((reference, index) => (
                        <Chip
                            key={`${reference.type}-${reference.id}-${index}`}
                            size="small"
                            label={`${translate('chatReferencePrefix')} [${reference.type}]: ${
                                reference.name || `#${reference.id}`
                            }`}
                            onDelete={() =>
                                setActiveRefs((prev) => prev.filter((_, i) => i !== index))
                            }
                            deleteIcon={<Close sx={{ fontSize: 16 }} />}
                            sx={{ fontWeight: 600, maxWidth: '100%' }}
                        />
                    ))}
                </Stack>
            ) : null}

            {canSuggestReply ? (
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 1.25,
                    }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={isSuggestionDisabled}
                        onClick={onSuggestReply}
                        startIcon={
                            isSuggestingReply ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <AutoAwesomeOutlined sx={{ fontSize: 17 }} />
                            )
                        }
                        sx={{ flexShrink: 0, textTransform: 'none' }}
                    >
                        {canRetryReplySuggestion
                            ? translate('retryReplySuggestion')
                            : translate('suggestReply')}
                    </Button>
                    {suggestionHint ? (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.4, whiteSpace: 'pre-wrap' }}
                        >
                            {suggestionHint}
                        </Typography>
                    ) : null}
                </Stack>
            ) : null}

            <Box
                sx={(theme) => ({
                    width: '100%',
                    flex: isStacked ? '1 1 0px' : '0 0 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: isStacked ? 'column' : 'row',
                    alignItems: isStacked ? 'stretch' : 'center',
                    gap: 1,
                    transition: theme.transitions.create(['gap'], {
                        duration: theme.transitions.duration.shorter,
                        easing: theme.transitions.easing.easeInOut,
                    }),
                })}
            >
                <Tooltip
                    title={!hasAccess ? translate('youAreNotAMemberOfThisChat') : ''}
                    placement="top"
                    disableHoverListener={hasAccess}
                >
                    <TextField
                        fullWidth
                        multiline
                        maxRows={isStacked ? undefined : 1}
                        inputRef={inputRef}
                        value={messageText}
                        disabled={isDisabled}
                        autoFocus={hasAccess}
                        placeholder={`${translate('writeAMessage')}...`}
                        onChange={(event) => {
                            skipAutoExpandRef.current = false;
                            rememberSelection(event.target);
                            onMessageTextChange(event.target.value);
                        }}
                        onSelect={(event) => rememberSelection(event.target)}
                        onKeyDown={handleKeyDown}
                        sx={{
                            width: '100%',
                            flex: '1 1 auto',
                            minWidth: 0,
                            minHeight: 0,
                            ...(isStacked
                                ? {
                                      display: 'flex',
                                      flexDirection: 'column',
                                      '& .MuiInputBase-root': {
                                          flex: 1,
                                          alignItems: 'stretch',
                                          height: '100%',
                                          minHeight: 0,
                                          width: '100%',
                                      },
                                      '& textarea': {
                                          minHeight: '100% !important',
                                          height: '100% !important',
                                          overflow: 'auto !important',
                                          width: '100%',
                                      },
                                  }
                                : {
                                      '& .MuiInputBase-root': { alignItems: 'center' },
                                  }),
                        }}
                    />
                </Tooltip>

                <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                        flexShrink: 0,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        alignSelf: isStacked ? 'flex-end' : 'center',
                    }}
                >
                    <Tooltip title={nextSizeLabel}>
                        <IconButton
                            onClick={handleSizeChange}
                            aria-label={nextSizeLabel}
                            sx={ACTION_BUTTON_SX}
                        >
                            {size === 'full' ? (
                                <CloseFullscreen fontSize="small" />
                            ) : (
                                <OpenInFull fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={isDisabled || !messageText.trim()}
                        aria-label={translate('sendMessage')}
                        sx={SEND_BUTTON_SX}
                    >
                        {isSending ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            <Send fontSize="small" />
                        )}
                    </IconButton>
                </Stack>
            </Box>
        </Box>
    );
};
